import { c as _c } from "react/compiler-runtime";
import { getCSSVariable, storage } from "@/utils/helpers";
import { createContext, ReactNode, startTransition, useContext, useEffect, useState } from "react";
export type Theme = "light" | "dark";
export const THEMES: { [key in Theme]: Theme } = {
  light: "light",
  dark: "dark"
};
const THEME_COLOR_META_SELECTOR = 'meta[name="theme-color"]';
const CANVAS_CSS_VARIABLE = "--color-canvas";
const syncThemeColorMeta = () => {
  const meta = document.querySelector<HTMLMetaElement>(THEME_COLOR_META_SELECTOR);
  const canvas = getCSSVariable(CANVAS_CSS_VARIABLE);
  if (meta && canvas) meta.content = canvas;
};
const isValidTheme = (value: unknown): value is Theme => typeof value === "string" && value in THEMES;
const getInitialTheme = (): Theme => {
  if (typeof window === "undefined") return THEMES.light;
  const savedTheme = storage.getItem("theme");
  if (isValidTheme(savedTheme)) return savedTheme;
  const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  switch (systemPrefersDark) {
    case true:
      return THEMES.dark;
    case false:
      return THEMES.light;
  }
};
interface ThemeContextType {
  theme: Theme;
  mounted: boolean;
  updateTheme: (newTheme: Theme) => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
export const ThemeProvider = t0 => {
  const $ = _c(13);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getInitialTheme();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [theme, setTheme] = useState(t1);
  const [mounted, setMounted] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = newTheme => {
      if (isValidTheme(newTheme)) {
        setTheme(newTheme);
        storage.setItem("theme", newTheme);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updateTheme = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      startTransition(() => {
        setMounted(true);
      });
      const syncTheme = () => {
        const savedTheme = storage.getItem("theme");
        if (isValidTheme(savedTheme)) {
          setTheme(savedTheme);
          document.documentElement.setAttribute("data-theme", savedTheme);
        }
      };
      syncTheme();
      const handleStorageChange = e => {
        if (e.key === "theme") {
          syncTheme();
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== theme) {
    t5 = () => {
      document.documentElement.setAttribute("data-theme", theme);
      syncThemeColorMeta();
    };
    t6 = [theme];
    $[4] = theme;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== mounted || $[8] !== theme) {
    t7 = {
      theme,
      mounted,
      updateTheme
    };
    $[7] = mounted;
    $[8] = theme;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== children || $[11] !== t7) {
    t8 = <ThemeContext.Provider value={t7}>{children}</ThemeContext.Provider>;
    $[10] = children;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
export const useThemeCntx = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeCntx must be used within a ThemeProvider");
  }
  return context;
};